import React, { FC, useEffect, useMemo } from 'react'
import {
  getParentDepartment,
  useLuxLabel,
  useLuxMeasure,
} from '@moonpig/web-core-utils'
import {
  DispatchCutOffQuery,
  PageTypeEnum,
  ProductModalProvider,
} from '@moonpig/web-shared-products'
import { useLoggedIn } from '@moonpig/web-core-auth'
import { useFlags } from '@moonpig/web-explore-flags'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { Search } from '../../components/Search'
import { useSearchStore } from '../../store/SearchStore'
import { FilterSelectionError } from '../../components/FilterError/FilterErrorMessage'
import { useLuxAddExperimentData } from '../../utils'
import { SEARCH_PAGE_TYPE } from '../../constants'
import { useFindLocaleText } from '../../text-localisation'
import { SelectedSuggestion } from '../../components/types'

export const SearchWrapper: FC<{
  luxLabel: string
  useClientSideRouting: boolean
  experiments?: { [key: string]: string } | undefined
  dispatchCutOffData?: DispatchCutOffQuery
  pageTitle: string
  pageType: string
  groupCardProject?: string
  department: DepartmentsEnum[]
  searchTerm: string
  selectedSuggestion?: SelectedSuggestion
}> = ({
  luxLabel,
  useClientSideRouting,
  experiments,
  dispatchCutOffData,
  pageTitle,
  pageType,
  groupCardProject,
  department,
  searchTerm,
  selectedSuggestion,
}) => {
  const flags = useFlags()
  const localiseText = useFindLocaleText()
  const { loggedIn } = useLoggedIn()
  const filtersFromStore = useSearchStore(store => store.selectedFilters)
  const resetFilteringEvent = useSearchStore(store => store.resetFilteringEvent)
  const results = useSearchStore(store => store.results)
  const resultsLoading = useSearchStore(store => store.loading)
  const hasFilterSelectionError = useSearchStore(
    store => store.filterSelectionError,
  )
  const clearFilterError = useSearchStore(store => store.clearFilterError)

  const searchFilters = filtersFromStore.map(f => ({
    key: f.id,
    group: f.parent,
    userApplied: true,
  }))

  const searchFiltersForContext = useMemo(() => {
    return searchFilters.map(f => ({
      facetKey: f.key,
      group: f.group,
      userApplied: f.userApplied,
    }))
  }, [searchFilters])

  useLuxLabel(luxLabel)
  useLuxMeasure(useClientSideRouting)
  const luxAbTestCustomData = [
    ...Object.entries(experiments ?? {}).map(([name, value]) => ({
      name: `experiment_${name}`,
      value,
    })),
    {
      name: 'search_department',
      value: department.length
        ? getParentDepartment(department[0])
        : 'ALL_CARDS',
    },
    {
      name: 'search_product_count',
      value: results.totalCount.toString(),
    },
    {
      name: 'search_applied_filters',
      value: filtersFromStore.length.toString(),
    },
    {
      name: 'user_is_authenticated',
      value: loggedIn.toString(),
    },
  ]
  useLuxAddExperimentData(luxAbTestCustomData)

  useEffect(() => {
    resetFilteringEvent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedSuggestion])

  return (
    <ProductModalProvider
      context={{
        flags,
        location: pageTitle,
        pageType: PageTypeEnum.SEARCH,
        filters: searchFiltersForContext,
      }}
    >
      {hasFilterSelectionError && (
        <FilterSelectionError onDismiss={clearFilterError} />
      )}

      <Search
        products={results.products}
        experiments={experiments}
        suggestions={results.suggestions}
        dispatchCutOffData={dispatchCutOffData}
        metaTitle={
          pageType === SEARCH_PAGE_TYPE
            ? localiseText('find.search_results')
            : pageTitle
        }
        groupCardProject={groupCardProject}
        isDynamic={pageType !== SEARCH_PAGE_TYPE}
        totalClickRankDocumentCount={results.totalClickRankDocumentCount}
        loading={!!resultsLoading}
        totalCount={results.totalCount}
        department={department[0]}
      />
    </ProductModalProvider>
  )
}
